import { calendarDate } from '../../shared/dateTime';
import { FORKLIFT_FILTER_CHOICES } from './forklift';

/**
 * Znaczniki wiersza banku — filtr „Znaczniki” i alerty czekające na
 * potwierdzenie (omówienie 01.10.2026, przegląd przed prezentacją).
 *
 * Alert o przeplanowaniu i zmianę w TMS po obsadzeniu planista widział
 * dotąd dopiero na karcie zlecenia albo w dzwonku. Teraz wiersz listy ma
 * ikonę, a filtr zbiera zlecenia z alertem przeplanowania w jednym widoku.
 * Co „czeka na potwierdzenie”, liczy API tą samą regułą co baner na karcie
 * (`apps/api/src/user-offers/alert-markers.ts`); tu jest tylko odczyt.
 */

/**
 * „Z alertem przeplanowania” — nowa wartość klucza `forklift` filtra
 * „Znaczniki” (klucz i wartości wózkowe react-admin zapamiętał
 * użytkownikom, więc zostają). Kontrakt z API (`MARKERS_FILTER_REPLAN`
 * w `user-offers/filters.ts`), pilnuje go `markers.test.ts`.
 */
export const MARKERS_FILTER_REPLAN = 'replan';

/**
 * „Straciły pojazd” — czerwona lista (omówienie 01.10.2026). Kolejna wartość
 * klucza `forklift`, kontrakt z API (`MARKERS_FILTER_LOST_VEHICLE`
 * w `user-offers/filters.ts`), pilnuje go `markers.test.ts`.
 */
export const MARKERS_FILTER_LOST_VEHICLE = 'lostVehicle';

/**
 * „Do sprzedaży” — zlecenia STANDARD i MEGA w planowaniu FTL (odpowiedzi
 * Marathonu 07.10.2026). Kolejna wartość klucza `forklift`, kontrakt z API
 * (`MARKERS_FILTER_FOR_SALE` w `user-offers/filters.ts`), pilnuje go
 * `markers.test.ts`.
 */
export const MARKERS_FILTER_FOR_SALE = 'forSale';

/**
 * Opcje filtra: wózki jak dotąd (`forklift.ts`), alert przeplanowania,
 * czerwona lista, rekomendowane do sprzedaży.
 */
export const MARKERS_FILTER_CHOICES = [
  ...FORKLIFT_FILTER_CHOICES,
  { id: MARKERS_FILTER_REPLAN, name: 'Z alertem przeplanowania' },
  { id: MARKERS_FILTER_LOST_VEHICLE, name: 'Straciły pojazd' },
  { id: MARKERS_FILTER_FOR_SALE, name: 'Do sprzedaży' },
];

/**
 * Opcje filtra bez „Do sprzedaży”, gdy przełącznik `saleRecommendation` jest
 * wyłączony (wersja „obecna” = produkcja 0.31.3; decyzja użytkownika
 * 12.10.2026). Wartość zapamiętaną przez react-admina API i tak traktuje
 * wtedy jak domyślne „ukryj czynności wózkowe”.
 */
export function saleFilterChoices<C extends { id: string }>(
  choices: readonly C[],
  saleOn: boolean,
): C[] {
  return choices.filter((c) => saleOn || c.id !== MARKERS_FILTER_FOR_SALE);
}

/**
 * Opcje filtra dla zalogowanego. Alert o przeplanowaniu widzi tylko
 * uprawnienie „Obsadzanie pojazdem” (`FTL_ASSIGN` — decyzja użytkownika
 * 12.10.2026: „alert przeplanowania — opcja: widzi tylko master”); bez niego
 * API i tak oddaje pustą listę, więc opcji nie pokazujemy.
 */
export function markersFilterChoices<C extends { id: string }>(
  choices: readonly C[],
  seesReplan: boolean,
): C[] {
  return choices.filter((c) => seesReplan || c.id !== MARKERS_FILTER_REPLAN);
}

/**
 * Dymek ikony alertu o przeplanowaniu — nazwa jak tytuł banera na karcie,
 * dalej pełne zdanie zamiast „X — szczegóły na karcie zlecenia” (poprawa
 * brzmienia 01.10.2026).
 */
export const REPLAN_ALERT_HINT =
  'Rekomendowane przeplanowanie. Szczegóły znajdziesz na karcie zlecenia.';

/**
 * Dymek czerwonej ikony przeplanowania — alert z pewnym powodem z kartoteki
 * TMS (decyzja użytkownika 12.10.2026: „wyróżnij te ważniejsze alerty”).
 * Nagłówek jak na czerwonej karcie zlecenia.
 */
export const REPLAN_IMPORTANT_ALERT_HINT =
  'Pilne przeplanowanie: pewny powód z kartoteki TMS (np. nieobecny kierowca albo pojazd poza eksploatacją). Szczegóły znajdziesz na karcie zlecenia.';

/** Dymek ikony zmiany pojazdu w TMS po obsadzeniu (12.10.2026). */
export const VEHICLE_CHANGE_ALERT_HINT =
  'Pojazd zmienił się w TMS po obsadzeniu (zestaw, eksploatacja, dostępność albo inny pojazd w TMS niż u nas) — czeka na potwierdzenie.';

/** Dymek ikony zmiany w TMS — nazwa jak tytuł banera na karcie. */
export const TMS_CHANGE_ALERT_HINT = 'Zmiana w TMS po obsadzeniu czeka na potwierdzenie.';

/**
 * Dopisek, gdy wśród zmian w TMS jest podejrzenie anulacji — baner na
 * karcie ma przy nim osobne decyzje. TMS nie oznacza anulacji wprost,
 * więc „możliwa”, nie „anulowano”.
 */
export const TMS_CANCELLATION_SUFFIX = ' Wśród zmian jest możliwa anulacja.';

/**
 * Dymek, gdy TMS sam podał anulację (lista anulowanych, przełącznik
 * `tmsCancellations`, 19.10.2026) — fakt, nie „możliwa”; decyzja na karcie.
 */
export const TMS_CANCELLED_HINT = 'Zlecenie anulowane w TMS — czeka na decyzję na karcie zlecenia.';

/** Ile alertów wiersza czeka na potwierdzenie na karcie — pole `pendingAlerts` z API. */
export interface PendingAlerts {
  /** Rekomendowane przeplanowanie. */
  replan: number;
  /** Zmiana w TMS po obsadzeniu. */
  change: number;
  /** Podejrzenie anulacji w TMS. */
  cancellation: number;
  /** Zmiana pojazdu w TMS po obsadzeniu (12.10.2026). */
  vehicleChange: number;
  /**
   * Ile przeplanowań jest ważnych — pewny powód z kartoteki TMS (reguła
   * `apps/api/src/user-offers/alert-importance.ts`, 12.10.2026).
   */
  replanImportant: number;
  /**
   * Ile anulacji potwierdził TMS listą anulowanych (19.10.2026) — wliczone
   * też w `cancellation`.
   */
  cancelledInTms: number;
}

/**
 * Dymek ikony alertów z TMS: anulowane w TMS (fakt z listy anulowanych),
 * możliwa anulacja (zdjęte obsadzenie) albo sama zmiana.
 */
export function tmsMarkerTitle(alerts: Pick<PendingAlerts, 'cancellation' | 'cancelledInTms'>): string {
  if (alerts.cancelledInTms > 0) return TMS_CANCELLED_HINT;
  return alerts.cancellation > 0 ? `${TMS_CHANGE_ALERT_HINT}${TMS_CANCELLATION_SUFFIX}` : TMS_CHANGE_ALERT_HINT;
}

const count = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

/**
 * Odczyt `pendingAlerts` z wiersza listy. Brak pola albo śmieci to zera —
 * znacznik pokazujemy tylko wtedy, gdy API mówi, że coś czeka.
 */
export function pendingAlertsOf(record: Record<string, unknown>): PendingAlerts {
  const raw = record.pendingAlerts;
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    replan: count(o.replan),
    change: count(o.change),
    cancellation: count(o.cancellation),
    vehicleChange: count(o.vehicleChange),
    replanImportant: count(o.replanImportant),
    cancelledInTms: count(o.cancelledInTms),
  };
}

/**
 * Od kiedy zlecenie jest bez pojazdu, który miało — pole `lostVehicleAt`
 * z API (reguła `apps/api/src/user-offers/vehicle-loss.ts`). `null`, gdy
 * zlecenia nie ma na czerwonej liście albo pole ma zły kształt.
 */
export function lostVehicleAtOf(record: Record<string, unknown>): string | null {
  const v = record.lostVehicleAt;
  if (typeof v !== 'string' || v === '') return null;
  return Number.isNaN(new Date(v).getTime()) ? null : v;
}

/** Dymek czerwonej ikony — od kiedy (czas polski) i co dalej. */
export function lostVehicleHint(sinceText: string): string {
  return `Zlecenie straciło pojazd ${sinceText} i czeka na ponowne obsadzenie.`;
}

/**
 * Rekomendacja sprzedaży — pole `saleRecommendation` z API (reguła
 * `apps/api/src/user-offers/sale-recommendation.ts`, odpowiedzi Marathonu
 * 07.10.2026). `null`, gdy zlecenie nie jest rekomendowane albo pole ma zły
 * kształt. Rekomendacja, nie automat: do Trade przekazuje człowiek.
 */
export interface SaleRecommendation {
  vehicleType: string;
  text: string;
}

export function saleRecommendationOf(record: Record<string, unknown>): SaleRecommendation | null {
  const raw = record.saleRecommendation;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.text !== 'string' || o.text === '') return null;
  return { vehicleType: typeof o.vehicleType === 'string' ? o.vehicleType : '', text: o.text };
}

/** Napis znacznika na liście i nagłówek na karcie. */
export const SALE_RECOMMENDATION_LABEL = 'Do sprzedaży';

/**
 * Alert przed załadunkiem — pole `loadingAlert` z API (reguła
 * `apps/api/src/user-offers/loading-alert.ts`, odpowiedzi Marathonu
 * 07.10.2026: powiadomienie o 15:00 w dniu poprzedzającym załadunek).
 * `null`, gdy alertu nie ma, zlecenie ma już wykonawcę albo pole ma zły
 * kształt.
 */
export interface LoadingAlert {
  /** Dzień załadunku `YYYY-MM-DD` (czas polski). */
  loadingDay: string;
  /** `tomorrow` — dzień przed załadunkiem, `today` — w dniu załadunku. */
  when: 'tomorrow' | 'today';
}

export function loadingAlertOf(record: Record<string, unknown>): LoadingAlert | null {
  const raw = record.loadingAlert;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.loadingDay !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(o.loadingDay)) return null;
  if (o.when !== 'tomorrow' && o.when !== 'today') return null;
  return { loadingDay: o.loadingDay, when: o.when };
}

/**
 * Nagłówek znacznika — „jutro” przed dniem załadunku, „dziś” w jego dniu
 * (alert podnosi przegląd o 15:00 dzień wcześniej, a znacznik trwa do końca
 * dnia załadunku, dopóki zlecenie jest bez wykonawcy).
 */
export function loadingAlertTitle(alert: LoadingAlert): string {
  return alert.when === 'today' ? 'Dziś załadunek, bez auta' : 'Jutro załadunek, bez auta';
}

/** Dymek ikony na liście i treść na karcie. */
export function loadingAlertHint(alert: LoadingAlert): string {
  return `${loadingAlertTitle(alert)} — załadunek ${calendarDate(alert.loadingDay)}, a zlecenie nadal nie ma obsadzonego pojazdu ani przewoźnika.`;
}
